import * as React from 'react';
import {useState} from 'react';
import './index.sass';
import {Layout} from './Components/Layout/Layout';
import {AccountComponent, TabBarComponent} from './lib/components';
import {useDispatch, useSelector} from 'react-redux';
import {Dispatch, RootState} from './Redux/store';
import {Tabs} from './lib/components/tabbar';
import {TopBar} from './Components/TopBar/TopBar';
import {auth} from './Firebase/main';
import {ConnectButton} from './Components/Buttons/ConnectButton';
import {signOut} from './Authentication/fb';
import {connect} from './Authentication';
import {parseNotifications} from './Messages';
import {createUserInstance, isUserId, parseUsers} from './Users';
import {Profile} from './lib/components/profile';
import {Chat, IChatProps} from './Components/Chat/Chat';
import {List} from './Components/List/List';
import {IInputProps, InputComponent} from './Components/Input/Input';
import {Icon} from '@iconify/react';
import {eth} from './Authentication/eth';
import {ThreadRender} from './Components/Thread/Thread';


export default function App() {
    //Local state
    const [selectedTab, setSelectedTab] = useState<Tabs>(Tabs.home);
    const [minimized, setMinimized] = useState<boolean>(false);
    const [accountExpanded, setAccountExpanded] = useState<boolean>(true);
    const [isAccountLoading, setIsAccountLoading] = useState<boolean>(false);
    const [globalLoading, setGlobalLoading] = useState<boolean>(true);

    const dispatch = useDispatch<Dispatch>();

    //Effects
    React.useEffect(() => {
        const {chats: {getRooms}, users: {getUsers}, auth: {handleAuth}} = dispatch;

        getRooms()
            .then(getUsers)
            .then(handleAuth)
            .then(() => {
                setGlobalLoading(false);
            });

        eth && eth.on('accountsChanged', async ([acc]: string[]) => {
            const currentId = auth.currentUser?.uid;
            if (acc && currentId) {
                if (acc !== currentId) {
                    setAccountExpanded(true);
                    setIsAccountLoading(true);
                    await handleConnect();
                }
            }
        });
    }, []);

    //Global state
    const Rooms = useSelector((state: RootState) =>
        Object.values(state.chats.rooms),
    );
    const Main = useSelector((state: RootState) => state.main);
    const Users = useSelector(parseUsers);
    const Notifications = useSelector(parseNotifications);


    //Methods
    async function handleConnect(): Promise<void> {
        setIsAccountLoading(true);
        await connect();
        setIsAccountLoading(false);
    }


    function onTabSelect(tab: Tabs): void {
        switch (tab) {
            case Tabs.home:
                dispatch.main.update({selectedId: Main.bufferId});
                break;
            case Tabs.buddies:
                dispatch.main.update({selectedId: '', bufferId: Main.selectedId});
                break;
        }
        setSelectedTab(tab);
    }


    function handleInputSubmit(value: string | any): void {
        const empty: boolean = !value.replace(/^\s+|\s+$/g, '').length;

        if (empty) {
            return;
        }

        dispatch.messages.sendMessage({
            message: value,
            receiver: Main.selectedId,
        });
    }


    function getHeaderProps(ID: string): Partial<IChatProps> {
        let result: Partial<IChatProps> = {};
        if (isUserId(ID)) {
            const selectedUser = Users.all.find((user) => user.uid === ID);
            if (selectedUser) {
                result = {
                    name: selectedUser.name,
                    status_text: selectedUser.uid,
                    avatar_url: selectedUser.avatar.face,
                    online: selectedUser.online,
                    buddy: Users.buddies.includes(ID),

                    onStatusTextClick: () => window.open(`https://opensea.io/${ID}`),
                    onCopyIconClick: () => navigator.clipboard.writeText(ID),

                };
            }
        }

        else {
            const selectedRoom = Rooms.find((room) => room.id === ID);
            if (selectedRoom) {
                result = {
                    name: selectedRoom.title,
                    status_text: selectedRoom.id === '_roomsale' ? '$99/month' : selectedRoom.link?.replace('https://', '') || 'public',
                    onStatusTextClick: selectedRoom.link ? () => window.open(selectedRoom.link) : undefined,
                    avatar_type: 'room',
                    avatar_url: selectedRoom.avatar,
                };
            }
        }

        return result;
    }


    function getInputProps(ID: string): IInputProps {

        if (isUserId(ID)) {
            let selectedUser = Users.all.find((user) => user.uid === ID);

            if (!selectedUser) {
                selectedUser = createUserInstance({uid: ID});
            }

            const {blacklist, uid} = Users.my;

            const isBlocked = selectedUser.blacklist.includes(uid) || blacklist.includes(ID);

            return {
                onSubmit: !auth.currentUser ? handleConnect : isBlocked ? console.log : handleInputSubmit,
                placeholder: isBlocked ? `Messaging with ${selectedUser.name} is blocked` : !auth.currentUser ? `Connect to send private messages` : `Send message to ${selectedUser.name}`,
                disabled: isBlocked || !auth.currentUser,
                icon: !auth.currentUser ? 'noto-v1:fox' : isBlocked ? 'fluent:block-24-filled' : 'fluent:arrow-enter-left-20-filled',
            };
        }

        else {
            const selectedRoom = Rooms.find((room) => room.id === ID);

            if (!selectedRoom) {
                throw new Error('Room not found');
            }

            const sendToWebSite = selectedRoom.link && !window.location.href.includes(selectedRoom.link);
            const isDisabled = sendToWebSite || !auth.currentUser;

            return {
                onSubmit: sendToWebSite ? () => window.open(selectedRoom.link) : !auth.currentUser ? handleConnect : handleInputSubmit,
                placeholder: sendToWebSite ? `Go to ${selectedRoom.link?.replace('https://', '')}`
                    : !auth.currentUser ? 'Connect to send messages'
                        : `Send message to ${selectedRoom.title}`,
                disabled: isDisabled,
                icon: sendToWebSite ? 'octicon:link-external-24'
                    : !auth.currentUser ? 'noto-v1:fox'
                        : 'fluent:arrow-enter-left-20-filled',
            };
        }
    }


    return (
        <Layout
            loading={globalLoading}
            accountExpanded={accountExpanded}
            minimized={minimized}
            TopBar={<TopBar onClick={() => setMinimized(!minimized)} minimized={minimized}/>}
            TabBar={
                <TabBarComponent
                    selectedTab={selectedTab}
                    onSelect={onTabSelect}
                    notifications={{
                        [Tabs.home]: Notifications[Tabs.home],
                        [Tabs.buddies]: Notifications[Tabs.buddies],
                    }}/>
            }
            Header={
                Main.selectedId &&
                <>
                  <Chat {...getHeaderProps(Main.selectedId)}/>
                    {isUserId(Main.selectedId) && !Users.buddies.includes(Main.selectedId) &&
                    <span
                      className={'header-buddy'}
                      onClick={() => dispatch.users.toggleBuddy({buddyId: Main.selectedId, myBuddies: Users.buddies})}>
                      <Icon icon={'majesticons:user'} height={32}/>
                      <Icon className={'header-buddy-plus'} height={18} icon={'entypo:plus'}/>
                    </span>
                    }
                </>
            }
            List={
                <List notifications={Notifications} selectedRoom={Main.selectedRoom} tab={selectedTab}
                      rooms={Rooms}
                      allUsers={Users.all} buddyIds={Users.buddies}
                      blockedIds={Users.blacklist} addressIds={Users.addresses} selectedId={Main.selectedId}
                      myId={Users.my.uid}/>
            }


            Thread={
                Main.selectedId ? (
                    Main.viewProfile ?
                        <Profile
                            avatar={
                                Users
                                    .all
                                    .find((user) => user.uid === Main.selectedId)
                                    ?.avatar
                                    .body
                            }
                        /> : <ThreadRender cid={Main.selectedId}/>
                ) : undefined
            }
            Account={
                !auth.currentUser || isAccountLoading ? (
                    <ConnectButton onPress={handleConnect} loading={isAccountLoading}/>
                ) : (
                    <AccountComponent
                        onSignOut={signOut}
                        name={Users.my.name}
                        uid={Users.my.uid}
                        avatarUrl={Users.my.avatar.face}
                        onProfilePress={() => dispatch.main.update({selectedId: Users.my.uid, viewProfile: true})}
                        expanded={accountExpanded}
                        onMenuClick={() => setAccountExpanded(!accountExpanded)}
                    />
                )
            }
            Input={Main.selectedId && <InputComponent {...getInputProps(Main.selectedId)}/>}
        />
    );
}
